import { effect, inject, untracked } from '@angular/core';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import { Me } from '@iw/core/entities';
import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@iw/store';

import { USER_API } from './api';
import { UpdateUserReq } from './models';

interface UserState {
  user: Me;
}

const initialState: UserState = {
  user: null,
};

export const UserStore = signalStore(
  withDevtools('user'),
  withState(initialState),
  withRequestStatus(['getMe', 'updateUser']),
  withRequestHandler(),
  withProps((store) => ({
    store,
    userApi: inject(USER_API),
    storeHelperService: inject(StoreHelperService),
    spinnerService: inject(SpinnerService),
  })),
  withMethods(({ store, userApi }) => ({
    getMe: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getMe'));
        return userApi.getUser().pipe(
          tapResponse({
            next: (user) => {
              patchState(store, { user }, setSuccessStatusFor('getMe'));
            },
            error: (error: AppError) =>
              patchState(store, setErrorStatusFor('getMe', error)),
          }),
        );
      }),
    ),

    updateUser: rxMethod<UpdateUserReq>(
      switchMap((params) => {
        patchState(store, setPendingStatusFor('updateUser'));
        return userApi.updateUser(params).pipe(
          tapResponse({
            next: () => {
              patchState(store, setSuccessStatusFor('updateUser'));
            },
            error: (error: AppError) =>
              patchState(store, setErrorStatusFor('updateUser', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
